/**
 * Google Sign-In using Google Identity Services (GIS).
 * Uses VITE_GOOGLE_CLIENT_ID — no Firebase required.
 * The id_token (credential JWT) is verified directly by the backend.
 */

export interface SocialAuthResult {
  provider: 'google';
  idToken: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}

type CredentialCallback = (result: SocialAuthResult) => void;

let gisInitialized = false;
let registeredCallback: CredentialCallback | null = null;

/** Returns true when VITE_GOOGLE_CLIENT_ID is set. */
export function isGoogleSignInConfigured(): boolean {
  return !!import.meta.env.VITE_GOOGLE_CLIENT_ID;
}

function parseJwtPayload(token: string): Record<string, string> {
  try {
    return JSON.parse(atob(token.split('.')[1]!));
  } catch {
    return {};
  }
}

/** Internal — called by GIS when the user picks a Google account. */
function onCredentialResponse(response: { credential: string }) {
  if (!response.credential || !registeredCallback) return;
  const payload = parseJwtPayload(response.credential);
  registeredCallback({
    provider: 'google',
    idToken: response.credential,
    email: payload.email,
    name: payload.name,
    firstName: payload.given_name,
    lastName: payload.family_name,
  });
}

/**
 * Waits for the GIS script to be ready (up to ~3 s), then resolves.
 * Returns false if the script never loaded or client ID is missing.
 */
function waitForGis(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!isGoogleSignInConfigured()) { resolve(false); return; }

    const check = (remaining: number) => {
      if ((window as any).google?.accounts?.id) { resolve(true); return; }
      if (remaining <= 0) { resolve(false); return; }
      setTimeout(() => check(remaining - 100), 100);
    };
    check(3000);
  });
}

/**
 * Initialize GIS and render Google's sign-in button into the given container.
 *
 * Safe to call on every component mount — re-registers the callback so
 * repeated sign-in attempts (after cancellation) keep working.
 *
 * Returns true if the button was rendered successfully.
 */
export async function mountGoogleButton(
  container: HTMLElement,
  onSuccess: CredentialCallback,
): Promise<boolean> {
  const ready = await waitForGis();
  if (!ready) {
    console.warn('[GoogleAuth] GIS not available. Check VITE_GOOGLE_CLIENT_ID and script tag.');
    return false;
  }

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const gis = (window as any).google.accounts.id;

  // Always store the latest callback so fresh closures work after re-mounts.
  registeredCallback = onSuccess;

  // Only initialize once — updating registeredCallback above is enough for re-mounts.
  if (!gisInitialized) {
    gis.initialize({
      client_id: clientId,
      callback: onCredentialResponse,
      cancel_on_tap_outside: true,
    });
    gisInitialized = true;
  }

  gis.renderButton(container, {
    theme: 'outline',
    size: 'large',
    type: 'standard',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: Math.min(container.offsetWidth || 400, 400),
    text: 'continue_with',
  });

  return true;
}
